import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { Box, IconButton, Skeleton, Switch, Typography, useTheme } from '@mui/material';
import { MdOutlineLocalPhone, MdOutlineRemoveRedEye } from 'react-icons/md';
import { GoPlus } from 'react-icons/go';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import CallLogForm from 'pages/Business/Agent/CallLogs/components/CallLogForm';
import IntegrationRecords from './components/IntegrationRecords';
import AddContactDrawer from './components/AddContactDrawer';
import { useGetContacts, useUpdateContactMutation } from 'apis/business/agent/contact/hook';
import {
  CONTACT_CATEGORY_VALUES,
  ContactCategory,
  ContactRequest,
  ContactResponse
} from 'apis/business/agent/contact/types';
import { formatDate } from 'utils/helperFunctions';

interface FilterFormData {
  category: string;
}

type UpdatingField = 'vip' | 'do_not_call';

const defaultFormValues: FilterFormData = {
  category: 'all'
};

function displayNamePart(value: string | null | undefined): string {
  return value?.trim() || '-';
}

export default function Contacts() {
  const theme = useTheme();
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [filterSearchText, setFilterSearchText] = useState<string | null>(null);
  const [callLogId, setCallLogId] = useState<string>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [updatingContact, setUpdatingContact] = useState<{ id: number; field: UpdatingField } | null>(null);

  const selectedCategory = watch('category');

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  useEffect(() => {
    resetPage();
  }, [selectedCategory, resetPage]);

  const categoryFilterValue =
    selectedCategory !== 'all' ? CONTACT_CATEGORY_VALUES[selectedCategory as ContactCategory] : undefined;

  const {
    data: contactsResponse,
    isLoading,
    isFetching
  } = useGetContacts({
    ...(categoryFilterValue !== undefined && { 'filter[category_eq]': categoryFilterValue }),
    'filter[search_number_or_name]': filterSearchText || undefined,
    page: currentPage,
    per_page: perPage
  });

  const { mutate: updateContact, isPending } = useUpdateContactMutation();

  useEffect(() => {
    if (updatingContact != null && !isPending && !isFetching) {
      setUpdatingContact(null);
    }
  }, [updatingContact, isPending, isFetching]);

  const handleContactUpdate = (id: number, field: UpdatingField, payload: ContactRequest['contact']) => {
    setUpdatingContact({ id, field });
    updateContact({ id, payload: { contact: payload } });
  };

  const handleVipChange = (id: number, isVip: boolean) => {
    handleContactUpdate(id, 'vip', { category: isVip ? 'vip' : 'general' });
  };

  const handleDoNotCallChange = (id: number, doNotCall: boolean) => {
    handleContactUpdate(id, 'do_not_call', { do_not_call: doNotCall });
  };

  const handleOpenInteraction = (interactionId: number | null) => {
    if (!interactionId) return;
    setCallLogId(String(interactionId));
    setDrawerOpen(true);
  };

  return (
    <>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={{ xs: 2, lg: 3 }}
        gap={{ xs: 2, lg: 0 }}
      >
        <Box>
          <Typography variant='h6' fontWeight={500}>
            Contacts
          </Typography>
          <Typography color='text.secondary' fontSize={14} mt={0.5}>
            See contacts from past interactions and additions
          </Typography>
        </Box>
        <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width={{ xs: '100%', lg: 'auto' }}>
          <Box width={{ xs: '100%', lg: '280px' }}>
            <Search
              size='small'
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder='Search number or name'
              fullWidth
            />
          </Box>
          <Box width={{ xs: '100%', lg: '170px' }}>
            <Select
              name='category'
              label='Categories'
              control={control}
              sortOrder='none'
              options={[
                { value: 'all', label: 'All' },
                { value: 'vip', label: 'VIP' }
              ]}
              sx={{ height: '40px', background: theme.palette.background.paper }}
            />
          </Box>
          <Button
            variant='contained'
            color='primary'
            startIcon={<GoPlus />}
            onClick={() => setAddContactOpen(true)}
            sx={{ height: 40 }}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      <DataTable
        data={contactsResponse?.data}
        columns={columns(handleVipChange, handleDoNotCallChange, handleOpenInteraction, updatingContact)}
        isHeaderSticky
        isLoading={isLoading}
      />
      <Pagination
        totalPages={contactsResponse?.meta?.total_pages}
        totalCount={contactsResponse?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <CallLogForm
        key={`${callLogId}`}
        logId={callLogId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        showInteractionTypeFilter
        businessScoped
      />

      <AddContactDrawer open={addContactOpen} onClose={() => setAddContactOpen(false)} />
    </>
  );
}

const columns = (
  onVipChange: (id: number, isVip: boolean) => void,
  onDoNotCallChange: (id: number, doNotCall: boolean) => void,
  onOpenInteraction: (interactionId: number | null) => void,
  updatingContact: { id: number; field: UpdatingField } | null
): ColumnDef<ContactResponse>[] => [
  {
    header: 'NUMBER',
    cell: (info) => (
      <Box display='flex' alignItems='center' gap={1}>
        <MdOutlineLocalPhone size={18} color='#6B7280' />
        <Typography variant='body2'>{info.row.original.phone_number || '-'}</Typography>
      </Box>
    )
  },
  {
    header: 'FIRST NAME',
    cell: (info) => <Typography variant='body2'>{displayNamePart(info.row.original.first_name)}</Typography>
  },
  {
    header: 'LAST NAME',
    cell: (info) => <Typography variant='body2'>{displayNamePart(info.row.original.last_name)}</Typography>
  },
  {
    header: 'INTEGRATION RECORDS',
    cell: (info) => <IntegrationRecords records={info.row.original.associated_records} />
  },
  {
    header: 'IS VIP',
    cell: (info) =>
      updatingContact?.id === info.row.original.id && updatingContact.field === 'vip' ? (
        <Skeleton variant='rectangular' width={40} height={24} sx={{ borderRadius: '12px' }} />
      ) : (
        <Switch
          size='small'
          checked={info.row.original.category === 'vip'}
          onChange={(_, isVip) => onVipChange(info.row.original.id, isVip)}
          inputProps={{ 'aria-label': 'VIP status' }}
        />
      )
  },
  {
    header: 'DO NOT CALL',
    cell: (info) =>
      updatingContact?.id === info.row.original.id && updatingContact.field === 'do_not_call' ? (
        <Skeleton variant='rectangular' width={40} height={24} sx={{ borderRadius: '12px' }} />
      ) : (
        <Switch
          size='small'
          checked={!!info.row.original.do_not_call}
          onChange={(_, doNotCall) => onDoNotCallChange(info.row.original.id, doNotCall)}
          inputProps={{ 'aria-label': 'Do not call' }}
        />
      )
  },
  {
    header: 'CHAT',
    cell: (info) => <Typography>{info.row.original.chats_count ?? 0}</Typography>
  },
  {
    header: 'CALLS',
    cell: (info) => <Typography>{info.row.original.calls_count ?? 0}</Typography>
  },
  {
    header: 'LATEST CONVERSATION',
    cell: (info) => {
      const { latest_interaction_at } = info.row.original;
      return (
        <Typography variant='body2' whiteSpace='nowrap'>
          {latest_interaction_at ? formatDate(latest_interaction_at, 'MMM D, YYYY hh:mm A') : '-'}
        </Typography>
      );
    }
  },
  {
    header: 'ACTION',
    cell: (info) => {
      const { latest_interaction_id } = info.row.original;

      return (
        <IconButton
          size='small'
          aria-label='View interaction'
          disabled={!latest_interaction_id}
          onClick={() => onOpenInteraction(latest_interaction_id)}
        >
          <MdOutlineRemoveRedEye size={24} />
        </IconButton>
      );
    }
  }
];
